import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { Checkbox, Dropdown, Icon } from 'semantic-ui-react'
import { LoginPopupsQuery } from '~/QueryHooks/LoginPopupsQuery'
import { Category, ChangeRequestReviewType } from '~/api/services/loginPopups'
import Dialog from '~/components/Dialog'
import Layout from '~/components/Layout'
import TableComponent from '~/components/TableComponent'
import i18n from '~/components/i18n'
import { RouteTypes } from '~/routes/constants'
import { FiltersWrapper } from '../../ActivityLogs/style'
import {
  CellBadge,
  FieldName,
  Muted,
  Reviewer,
  StatusBadge,
  SummaryChip,
  SummaryWrapper,
  ValueText
} from './style'
import { El } from '~/components/Layout/style'

const ALL_CATEGORIES = 'all'

type StatusVariant = 'pending' | 'accepted' | 'rejected'

const STATUS_VARIANTS: Record<number, StatusVariant> = {
  0: 'pending',
  1: 'accepted',
  2: 'rejected'
}

const STATUS_ICONS: Record<StatusVariant, string> = {
  pending: 'clock outline',
  accepted: 'check circle outline',
  rejected: 'times circle outline'
}

const getStatusVariant = (status: number): StatusVariant => STATUS_VARIANTS[status] || 'pending'

const getCategoryLabel = (category: Category): string => {
  switch (category) {
    case Category.FarmerData:
      return i18n.t('Farmer data')
    case Category.ManagerData:
      return i18n.t('Manager data')
    case Category.UserData:
      return i18n.t('User data')
    default:
      return category
  }
}

const getTableColumnHeaders = () => [
  { name: '', key: 'select', type: 'change-request-select' },
  { name: i18n.t('Object type'), key: 'category', type: 'change-request-type' },
  { name: i18n.t('Field'), key: 'label', type: 'change-request-field' },
  { name: i18n.t('Action'), key: 'actionLabel', type: 'change-request-action' },
  { name: i18n.t('Old value'), key: 'oldValue', type: 'change-request-old-value' },
  { name: i18n.t('New value'), key: 'newValue', type: 'change-request-new-value' },
  { name: i18n.t('Status'), key: 'status', type: 'change-request-status' }
]

const ChangeRequestReview: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()

  const {
    data: fields,
    isLoading: isBusyFields,
    refetch: refetchFields,
    isFetching: isFetchingFields
  } = LoginPopupsQuery.getChangeDataRequestFields(id)

  const [categoryFilter, setCategoryFilter] = useState<string>(ALL_CATEGORIES)
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [acceptedFieldsMap, setAcceptedFieldsMap] = useState<Record<number, boolean>>({})

  useEffect(() => {
    if (fields) {
      setAcceptedFieldsMap(
        fields.reduce((acc, field) => {
          acc[field.id] = getStatusVariant(field.status) === 'accepted'
          return acc
        }, {} as Record<number, boolean>)
      )
    }
  }, [fields])

  const onMutationSuccess = () => refetchFields()
  const onRequestResolved = () => navigate(RouteTypes.DAIRY_FACTORY_CHANGE_REQUESTS)

  const { mutate: acceptField } = LoginPopupsQuery.acceptChangeDataRequestField(onMutationSuccess)
  const { mutate: rejectField } = LoginPopupsQuery.rejectChangeDataRequestField(onMutationSuccess)
  const { mutate: resolveRequest, isLoading: isResolving } =
    LoginPopupsQuery.resolveChangeDataRequest(onRequestResolved)

  const allRows: ChangeRequestReviewType[] = fields || []
  const rows = categoryFilter === ALL_CATEGORIES
    ? allRows
    : allRows.filter(field => field.category === categoryFilter)

  const hasPending = allRows.some(field => getStatusVariant(field.status) === 'pending')

  const buildResolvePayload = () => ({
    id,
    acceptedFields: allRows.filter(field => acceptedFieldsMap[field.id]).map(field => field.id),
    rejectedFields: allRows.filter(field => !acceptedFieldsMap[field.id]).map(field => field.id)
  })

  const toggleAcceptedField = (fieldId: number, checked: boolean) =>
    setAcceptedFieldsMap(prev => ({ ...prev, [fieldId]: checked }))

  const categoryOptions = [
    { key: ALL_CATEGORIES, value: ALL_CATEGORIES, text: t('All object types') },
    { key: Category.UserData, value: Category.UserData, text: t('User data') },
    { key: Category.ManagerData, value: Category.ManagerData, text: t('Manager data') },
    { key: Category.FarmerData, value: Category.FarmerData, text: t('Farmer data') }
  ]

  const summary: { variant: StatusVariant; label: string }[] = [
    { variant: 'pending', label: t('Pending') },
    { variant: 'accepted', label: t('Accepted') },
    { variant: 'rejected', label: t('Rejected') }
  ]

  const countByVariant = (variant: StatusVariant) =>
    allRows.filter(field => getStatusVariant(field.status) === variant).length

  function handleClickAction(row: ChangeRequestReviewType, type: string) {
    if (type === 'accept') {
      acceptField({ id, fieldId: row.id })
    } else if (type === 'reject') {
      rejectField({ id, fieldId: row.id })
    }
  }

  function renderColumn(type: string, value: any, row: ChangeRequestReviewType) {
    switch (type) {
      case 'change-request-select':
        return (
          <Checkbox
            checked={Boolean(acceptedFieldsMap[row.id])}
            onChange={(_, data) => toggleAcceptedField(row.id, Boolean(data.checked))}
          />
        )
      case 'change-request-type':
        return <CellBadge>{getCategoryLabel(row.category)}</CellBadge>
      case 'change-request-field':
        return (
          <>
            <FieldName>{row.label}</FieldName>
          </>
        )
      case 'change-request-action':
        return (
          <CellBadge>
            <Icon name={row.action === 'created' ? 'plus' : 'pencil'} />
            {row.actionLabel}
          </CellBadge>
        )
      case 'change-request-old-value':
        return value !== null && value !== undefined && value !== ''
          ? <ValueText strike>{String(value)}</ValueText>
          : <Muted>—</Muted>
      case 'change-request-new-value':
        return value ? <ValueText>{value}</ValueText> : <Muted>—</Muted>
      case 'change-request-status': {
        const variant = getStatusVariant(value)
        return (
          <StatusBadge variant={variant}>
            <Icon name={STATUS_ICONS[variant] as any} />
            {row.statusLabel}
          </StatusBadge>
        )
      }
      default:
        return <>{value}</>
    }
  }

  function renderSummary() {
    return (
      <SummaryWrapper>
        {summary.map(({ variant, label }) => (
          <SummaryChip key={variant} variant={variant}>
            <Icon name={STATUS_ICONS[variant] as any} />
            {label}: {countByVariant(variant)}
          </SummaryChip>
        ))}
      </SummaryWrapper>
    )
  }

  function renderFilters() {
    return (
      <FiltersWrapper>
        <Dropdown
          selection
          options={categoryOptions}
          value={categoryFilter}
          onChange={(_, data) => setCategoryFilter(data.value as string)}
        />
      </FiltersWrapper>
    )
  }

  function renderActions() {
    return (<div>
      <El.PageHeaderActionButton
        disabled={!hasPending || isResolving}
        onClick={() => setIsConfirmOpen(true)}
      >
        <Icon name='checkmark' />
        {t('Approve')}
      </El.PageHeaderActionButton>
    </div>
    )
  }

  function handleConfirm() {
    resolveRequest(buildResolvePayload())
  }

  function renderConfirmModal() {
    return (
      <Dialog
        type='abort'
        isDialogOpen={isConfirmOpen}
        isLoading={isResolving}
        textHeader={t('Approve change request')}
        textContent={t('Are you sure you want to resolve this request? Checked fields will be accepted and unchecked fields will be rejected.')}
        textSubmit={t('Approve')}
        textClose={t('Cancel')}
        handleSubmit={handleConfirm}
        handleClose={() => !isResolving && setIsConfirmOpen(false)}
      />
    )
  }

  return (
    <Layout
      isBusy={isBusyFields}
      title={t('Change request review')}
      segmented
      close={RouteTypes.DAIRY_FACTORY_CHANGE_REQUESTS}
      filters={renderFilters()}
      actions={renderActions()}
    >
      {renderSummary()}
      <TableComponent
        animated
        isFetchingData={isFetchingFields}
        tableColumnHeaders={getTableColumnHeaders()}
        rows={rows}
        total={rows.length}
        handleClickAction={handleClickAction}
        renderColumn={renderColumn}
        removePagination
      />
      {renderConfirmModal()}
    </Layout>
  )
}

export default ChangeRequestReview
